import { useMemo } from 'react'

import { fvDate } from '@fv/dates'

import type {
  FilterConfig,
  FilterOptionsHook,
  FilterSelectOption,
} from '@/components/CustomFilterForm/types'
import {
  getAllFilterNames,
  hasFilterValues,
  isMultiName,
} from '@/components/CustomFilterForm/types'

import { useResolvedLazyOptions } from './useLazyFilterOptions'

export type ActiveFilterSummary = { label: string; values: string[] }

function resolveValueLabel(
  value: string,
  optionSources: FilterSelectOption[],
  name: string,
): string {
  const option = optionSources.find(entry => entry.value === value)
  if (option) {
    return option.text
  }

  if (/date/i.test(name)) {
    const [start, end] = value.split('-').map(Number)

    if (!isNaN(start) && !isNaN(end)) {
      const startDate = fvDate(start).format('M/D/YY')
      const endDate = fvDate(end).format('M/D/YY')
      return `${startDate} - ${endDate}`
    }
  }

  return value
}

function getLazyOptions(
  filter: FilterConfig,
  resolvedOptions: Map<FilterOptionsHook, FilterSelectOption[]>,
): FilterSelectOption[] {
  if (!filter.useOptions) {
    return []
  }

  return resolvedOptions.get(filter.useOptions) ?? []
}

export function getActiveFilterSummaries(
  filterConfig: FilterConfig[],
  params: URLSearchParams,
  resolvedOptions: Map<FilterOptionsHook, FilterSelectOption[]> = new Map(),
): ActiveFilterSummary[] {
  return filterConfig
    .map(filter => {
      if (!hasFilterValues(filter, params)) {
        return null
      }

      const optionSources = [
        ...(filter.options ?? []),
        ...getLazyOptions(filter, resolvedOptions),
      ]

      const names = getAllFilterNames(filter)
      const multiNames = names.filter(isMultiName)
      const singleNames = names.filter(name => !isMultiName(name))

      const multiValues = multiNames.flatMap(name => params.getAll(name))
      if (multiValues.length) {
        const values = multiValues.map(value =>
          resolveValueLabel(value, optionSources, filter.name),
        )

        return { label: filter.label, values }
      }

      for (const name of singleNames) {
        const value = params.get(name)
        if (!value) continue

        return {
          label: filter.label,
          values: [resolveValueLabel(value, optionSources, name)],
        }
      }

      return null
    })
    .filter(Boolean) as ActiveFilterSummary[]
}

export function getFilterDescriptions(
  filterConfig: FilterConfig[],
  params: URLSearchParams,
  resolvedOptions: Map<FilterOptionsHook, FilterSelectOption[]> = new Map(),
): string[] {
  return getActiveFilterSummaries(filterConfig, params, resolvedOptions).map(
    ({ label, values }) => `${label}: ${values.join(', ')}`,
  )
}

function useResolvedFilterOptions(
  filterConfig: FilterConfig[],
  params: URLSearchParams,
) {
  const activeFilters = useMemo(
    () => filterConfig.filter(filter => hasFilterValues(filter, params)),
    [filterConfig, params],
  )

  return useResolvedLazyOptions(activeFilters)
}

export function useFilterDescriptions(
  filterConfig: FilterConfig[],
  params: URLSearchParams,
): string[] {
  const resolvedOptions = useResolvedFilterOptions(filterConfig, params)

  return useMemo(
    () => getFilterDescriptions(filterConfig, params, resolvedOptions),
    [filterConfig, params, resolvedOptions],
  )
}

export function useActiveFilterSummaries(
  filterConfig: FilterConfig[],
  queryString: string,
): ActiveFilterSummary[] {
  const params = useMemo(() => new URLSearchParams(queryString), [queryString])
  const resolvedOptions = useResolvedFilterOptions(filterConfig, params)

  return useMemo(
    () =>
      queryString
        ? getActiveFilterSummaries(filterConfig, params, resolvedOptions)
        : [],
    [queryString, filterConfig, params, resolvedOptions],
  )
}
